import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import {
  conversationStateValues,
  conversationTCAPI,
} from '@rsos/reduxDataStore/src/features/conversations/conversations.typings';

/**
 * Returns the agency participant IDs that belong to agencies OTHER than the
 * current user's own agency.
 *
 * A participant ID is considered "self" if it matches the session's
 * authority_account_id OR if it belongs to a TalkJS browser participant whose
 * custom.org_name matches authority_display_name. The latter handles the case
 * where the frontend registers users with their database integer ID (e.g. "32")
 * rather than the authority_account_id (e.g. "fqa_jv"), causing both IDs to
 * appear in agency_participants for the same agency.
 */
export function getOtherAgencyParticipants(
  conversation: conversationTCAPI,
): string[] {
  const { data, participants } = conversation;
  if (!data) return [];

  const { agency_participants, authority_account_id, authority_display_name } =
    data;
  const selfIds = new Set([authority_account_id]);

  for (const p of participants ?? []) {
    const orgName = (p.custom as { org_name?: string } | undefined)?.org_name;
    if (orgName && orgName === authority_display_name) {
      selfIds.add(String(p.id));
    }
  }

  return agency_participants.filter(id => !selfIds.has(id));
}

/**
 * Depending on the current state of the conversation and it's transfer status,
 * the action taken may either end the conversation for the citizen and the TC
 * or the current user will just leave the conversation and the other party can
 * still chat with each other. This method indicates which of those two actions
 * will be taken for the current user if they click the chat exit button.
 * @param conversation The conversation the action is sought for.
 */
export function getChatExitActionForCurrentUser(
  conversation: conversationStateValues | undefined,
): ChatExitAction {
  // if there is no conversation or the transfer has not been accepted, then the
  // user leaving the conversation will end it for everyone.
  if (conversation?.transfer?.status !== 'accepted') {
    return 'end';
  }
  if (
    !conversation.transfer.initiating_authority_account_id ||
    !conversation.transfer.receiving_authority_account_id
  ) {
    // if there is missing transfer authority data, default to ending the conversation to be safe
    // eslint-disable-next-line no-console -- log for sentry detection of missing data
    console.error(
      'Conversation transfer is accepted but is missing initiating or receiving authority account IDs',
      conversation.transfer,
    );
    return 'end';
  }
  if (
    conversation.source === SOURCE_TCAPI &&
    conversation.data &&
    getOtherAgencyParticipants(conversation).length === 0
  ) {
    return 'end';
  }

  return 'leave';
}

export const getChatButtonText = ({
  conversation,
  isConversationOngoing = false,
}: {
  conversation: conversationStateValues | undefined;
  isConversationOngoing: boolean;
}) => {
  if (isConversationOngoing) {
    const exitAction = getChatExitActionForCurrentUser(conversation);
    if (exitAction === 'leave') {
      return 'Leave Chat';
    }
    return 'End Chat';
  }

  switch (conversation?.source) {
    case SOURCE_TCAPI:
      return 'Accept Chat';
    default:
      return 'Start Chat';
  }
};

type ChatExitAction = 'leave' | 'end';
